import DynamicForm, { DynamicFormRef } from "@mavichpe/dynamic-forms";
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Col, Row } from "reactstrap";
import ProductsByCategoryFormConfig from "./form";
import { showAlert } from "@utils/Alert";
import { ProductsByCategoryFormDataType } from "@/formTypes/ProductsByCategoryFormDataType";
import {
  ALL_CATEGORIES_OPTION,
  encodeReportProperties,
  PDF_MAX_PROPERTIES,
  PRODUCT_REPORT_PROPERTIES,
  ProductReportPropertyKey,
  PROPERTY_FIELD_PREFIX
} from "@utils/ProductsByCategoryReport";

const DEFAULT_PROPERTIES: ProductReportPropertyKey[] = ["codigo", "descripcion_fact", "existencias", "costo"];

const ProductsByCategoryForm = () => {
  const formRef = useRef<DynamicFormRef>(null);
  const [categoryId, setCategoryId] = useState<string>(ALL_CATEGORIES_OPTION);
  const [includeInactive, setIncludeInactive] = useState<boolean>(false);
  const [selectedProperties, setSelectedProperties] = useState<Record<string, boolean>>(() => {
    return DEFAULT_PROPERTIES.reduce((a, key) => ({ ...a, [`${key}`]: true }), {});
  });

  useEffect(() => {
    formRef.current?.resetFormValue({
      categoryId: ALL_CATEGORIES_OPTION,
      includeInactive: false,
      ...DEFAULT_PROPERTIES.reduce((a, key) => ({ ...a, [`${PROPERTY_FIELD_PREFIX}${key}`]: true }), {})
    })
  }, [])

  const selectedKeys = useMemo((): ProductReportPropertyKey[] => {
    // El orden lo manda el catalogo, no el orden en que el usuario marca los checkbox.
    return PRODUCT_REPORT_PROPERTIES
      .filter(p => selectedProperties[`${p.key}`])
      .map(p => p.key);
  }, [selectedProperties])

  const onInputChange = (name: keyof ProductsByCategoryFormDataType, value: unknown) => {
    const fieldName = String(name);

    if (fieldName === "categoryId") {
      setCategoryId(`${value || ""}`);
      return;
    }

    if (fieldName === "includeInactive") {
      setIncludeInactive(!!value);
      return;
    }

    if (fieldName.startsWith(PROPERTY_FIELD_PREFIX)) {
      const key = fieldName.substring(PROPERTY_FIELD_PREFIX.length);
      setSelectedProperties(previous => ({ ...previous, [key]: !!value }));
    }
  }

  const setAllProperties = (checked: boolean) => {
    // `setFormValue` no desmarca un checkbox ya marcado, asi que se reinicia el
    // formulario completo conservando la categoria y el filtro de inactivos.
    formRef.current?.resetFormValue({
      categoryId,
      includeInactive,
      ...PRODUCT_REPORT_PROPERTIES.reduce((a, p) => ({ ...a, [`${PROPERTY_FIELD_PREFIX}${p.key}`]: checked }), {})
    })
    setSelectedProperties(PRODUCT_REPORT_PROPERTIES.reduce((a, p) => ({ ...a, [`${p.key}`]: checked }), {}));
  }

  const openReport = (format: "print" | "excel") => {
    if (!categoryId) {
      showAlert("danger", "Seleccione una categoria para generar el reporte");
      return;
    }

    if (selectedKeys.length === 0) {
      showAlert("danger", "Seleccione al menos una propiedad para generar el reporte");
      return;
    }

    const properties = encodeReportProperties(selectedKeys);
    const inactiveFlag = includeInactive ? "1" : "0";

    window.open(`/user/reports/${format}/products-by-category/${categoryId}/${properties}/${inactiveFlag}`, "_blank")
  }

  return (
    <>
      <p>
        Seleccione una categoria y las propiedades de los productos que desea incluir en el reporte
      </p>
      <DynamicForm<ProductsByCategoryFormDataType>
        ref={formRef}
        id="report"
        innerForm
        formConfig={ProductsByCategoryFormConfig}
        onInputChange={onInputChange}
      >
        <Row className="mb-3">
          <Col md={{ offset: 2 }}>
            <Button
              className="me-3"
              type="button"
              color="link"
              data-testid="select-all-properties"
              onClick={() => setAllProperties(true)}
            >
              Seleccionar todas
            </Button>
            <Button
              type="button"
              color="link"
              data-testid="clear-all-properties"
              onClick={() => setAllProperties(false)}
            >
              Limpiar
            </Button>
          </Col>
        </Row>
      </DynamicForm>

      {selectedKeys.length > PDF_MAX_PROPERTIES && (
        <Alert color="warning" data-testid="pdf-properties-warning">
          {`Selecciono ${selectedKeys.length} propiedades. El PDF incluira solo las primeras ${PDF_MAX_PROPERTIES}; el Excel las incluye todas.`}
        </Alert>
      )}

      <Row className="mt-3">
        <Col>
          <Button
            className="me-3"
            type="button"
            color="secondary"
            size="lg"
            data-testid="print-products-by-category"
            onClick={() => openReport("print")}
          >
            Imprimir PDF
          </Button>
          <Button
            type="button"
            color="success"
            size="lg"
            data-testid="excel-products-by-category"
            onClick={() => openReport("excel")}
          >
            Exportar a Excel
          </Button>
        </Col>
      </Row>
    </>
  )
}

export default ProductsByCategoryForm;
